import { Alert, Box, Button, Card, Stack, Typography } from '@mui/material';
import ExtensionRounded from '@mui/icons-material/ExtensionRounded';
import WbTwilightRounded from '@mui/icons-material/WbTwilightRounded';
import { Navigate, Link as RouterLink } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { MODULES } from '@/modules/registry';
import { useSession } from '@/platform/session';
import { ROLE_LABEL } from '@/platform/types';
import { PageHeader } from './PageHeader';

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

/** Home: each enabled module contributes its own widgets, per role. */
export function HomePage() {
  const { me, user, isStaff, isAdmin, isManager, hasModule, role, institution } = useSession();
  const firstName = user.name?.split(' ')[0] ?? '';
  const widgets = MODULES.filter((m) => hasModule(m.key))
    .map((m) => ({ key: m.key, Widget: isStaff ? m.HomeStaff : m.HomeStudent }))
    .filter((w) => w.Widget);

  // Reviewer-only accounts have nothing else here: straight to the review queue.
  if (role === 'reviewer') return <Navigate to="/revisao" replace />;
  // Students on their own (no institution) came for the ENEM module.
  if (user.independent && hasModule('enem')) return <Navigate to="/enem" replace />;

  return (
    <>
      <PageHeader
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}!`}
        subtitle={
          <>
            {ROLE_LABEL[role]}
            {institution && ` · ${institution.name}`}
            {role === 'student' && user.course && ` · ${user.course.name}`}
            {user.groups.length > 0 && ` · ${user.groups.map((g) => g.code ?? g.name).join(', ')}`}
          </>
        }
      />
      {isManager && me.pendingApprovals > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" component={RouterLink} to="/admin/approvals">
              Revisar
            </Button>
          }
        >
          {me.pendingApprovals === 1
            ? '1 professor(a) aguarda sua aprovação para entrar.'
            : `${me.pendingApprovals} professores aguardam sua aprovação para entrar.`}
        </Alert>
      )}
      {widgets.length === 0 ? (
        <Card>
          {isAdmin ? (
            <EmptyState
              icon={ExtensionRounded}
              title="Nenhum módulo ativo"
              description="A plataforma é modular: ative os módulos que sua instituição vai usar e eles aparecem para as pessoas na hora."
              action={
                <Button variant="contained" component={RouterLink} to="/admin">
                  Gerenciar módulos
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={WbTwilightRounded}
              title="Nada por aqui ainda"
              description="Quando a coordenação liberar atividades para você, elas aparecem nesta página."
            />
          )}
        </Card>
      ) : (
        <Stack spacing={4}>
          {widgets.map(({ key, Widget }) => (
            <Box key={key}>{Widget && <Widget />}</Box>
          ))}
        </Stack>
      )}
      {isStaff && widgets.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mt: 4, textAlign: 'center' }}
        >
          Módulos ativos: {MODULES.filter((m) => hasModule(m.key)).length}
          {isAdmin && (
            <>
              {' · '}
              <RouterLink to="/admin" style={{ color: 'inherit' }}>
                gerenciar
              </RouterLink>
            </>
          )}
        </Typography>
      )}
    </>
  );
}
